/**
 * RecurrenceEditor — kompakter UI fuer RFC-5545 RRULE-Strings.
 *
 * Deckt die vier haeufigsten Faelle ab:
 *   - Taeglich alle N Tage
 *   - Woechentlich alle N Wochen an bestimmten Wochentagen (BYDAY)
 *   - Monatlich alle N Monate — am Tag N (BYMONTHDAY) ODER am x-ten
 *     Wochentag (BYDAY=1MO/-1FR, "jeder 1. Montag im Monat";
 *     erweitert 2026-09-09 fuer wiederkehrende Aufgaben, Jörg: "1 voll")
 *   - Jaehrlich am Startdatum
 *
 * Endebedingung: nie / bis Datum (UNTIL) / nach N Wiederholungen (COUNT).
 *
 * Baut den RRULE-String beim onChange zusammen, der Caller schickt ihn
 * ans Backend (kalender.events.recurrence_rule). Der aktuelle Wert wird
 * nur nicht editier-persistent parsed — der Editor ist dumm, der Caller
 * initialisiert die Felder aus dem Bestand.
 */
import { useEffect, useState } from 'react';

export type RecurrenceFreq = 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY';
type EndMode = 'never' | 'until' | 'count';

interface Props {
  /** Startdatum des zugehoerigen Events — wird bei WEEKLY fuer BYDAY-Default gebraucht. */
  startDate: Date;
  /** Aktueller RRULE-String, wird beim Aendern via onChange durchgereicht. */
  value: string | null;
  onChange: (rrule: string | null) => void;
}

const WEEKDAYS: Array<{ key: string; label: string }> = [
  { key: 'MO', label: 'Mo' },
  { key: 'TU', label: 'Di' },
  { key: 'WE', label: 'Mi' },
  { key: 'TH', label: 'Do' },
  { key: 'FR', label: 'Fr' },
  { key: 'SA', label: 'Sa' },
  { key: 'SU', label: 'So' },
];

/** Wandelt Date → 'MO'/'TU'/... — Startdatums-Wochentag fuer BYDAY-Default. */
function weekdayCode(d: Date): string {
  // getDay: 0=So, 1=Mo, ... 6=Sa → Map auf RFC 5545 Codes
  return ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'][d.getDay()]!;
}

/** Monats-Modus: am festen Tag N vs. am x-ten Wochentag ("1. Montag"). */
type MonthlyMode = 'monthday' | 'weekday';

const ORDINALS: Array<{ value: number; label: string }> = [
  { value: 1, label: '1.' },
  { value: 2, label: '2.' },
  { value: 3, label: '3.' },
  { value: 4, label: '4.' },
  { value: -1, label: 'letzten' },
];

const WEEKDAY_LONG: Record<string, string> = {
  MO: 'Montag', TU: 'Dienstag', WE: 'Mittwoch', TH: 'Donnerstag',
  FR: 'Freitag', SA: 'Samstag', SU: 'Sonntag',
};

/** RRULE-String aus State bauen. */
function buildRrule(state: {
  freq: RecurrenceFreq;
  interval: number;
  byday: string[];
  endMode: EndMode;
  until: string;   // YYYY-MM-DD (leer bei never/count)
  count: number;
  monthlyMode: MonthlyMode;
  monthDay: number;       // 1-31 (monthday-Modus)
  monthOrdinal: number;   // 1..4 / -1 (weekday-Modus)
  monthWeekday: string;   // 'MO'.. (weekday-Modus)
}): string {
  const parts: string[] = [`FREQ=${state.freq}`];
  if (state.interval > 1) parts.push(`INTERVAL=${state.interval}`);
  if (state.freq === 'WEEKLY' && state.byday.length > 0) {
    parts.push(`BYDAY=${state.byday.join(',')}`);
  }
  if (state.freq === 'MONTHLY') {
    if (state.monthlyMode === 'weekday') {
      parts.push(`BYDAY=${state.monthOrdinal}${state.monthWeekday}`);
    } else {
      parts.push(`BYMONTHDAY=${state.monthDay}`);
    }
  }
  if (state.endMode === 'until' && state.until) {
    // RFC 5545: UNTIL im UTC-Format YYYYMMDDT235959Z
    const utc = state.until.replace(/-/g, '') + 'T235959Z';
    parts.push(`UNTIL=${utc}`);
  } else if (state.endMode === 'count' && state.count > 0) {
    parts.push(`COUNT=${state.count}`);
  }
  return parts.join(';');
}

/** RRULE-String → State parsen (defensiv, mit Defaults). */
function parseRrule(rrule: string | null, startDate: Date): {
  freq: RecurrenceFreq;
  interval: number;
  byday: string[];
  endMode: EndMode;
  until: string;
  count: number;
  monthlyMode: MonthlyMode;
  monthDay: number;
  monthOrdinal: number;
  monthWeekday: string;
} {
  const defaults = {
    freq: 'WEEKLY' as RecurrenceFreq,
    interval: 1,
    byday: [weekdayCode(startDate)],
    endMode: 'never' as EndMode,
    until: '',
    count: 10,
    monthlyMode: 'monthday' as MonthlyMode,
    monthDay: startDate.getDate(),
    monthOrdinal: 1,
    monthWeekday: weekdayCode(startDate),
  };
  if (!rrule) return defaults;
  const map = new Map<string, string>();
  for (const part of rrule.split(';')) {
    const [k, v] = part.split('=');
    if (k && v !== undefined) map.set(k.toUpperCase(), v);
  }
  const freq = (map.get('FREQ') as RecurrenceFreq) ?? defaults.freq;
  const interval = Number(map.get('INTERVAL') ?? '1') || 1;
  const bydayRaw = (map.get('BYDAY') ?? '').split(',').filter(Boolean);
  let endMode: EndMode = 'never';
  let until = '';
  let count = 10;
  if (map.has('UNTIL')) {
    endMode = 'until';
    // 'YYYYMMDDT...' → 'YYYY-MM-DD'
    const raw = map.get('UNTIL')!;
    until = `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`;
  } else if (map.has('COUNT')) {
    endMode = 'count';
    count = Number(map.get('COUNT')) || 10;
  }
  // Monats-Modus: BYDAY mit Ordinal-Praefix ("1MO"/"-1FR") = weekday-Modus,
  // BYMONTHDAY = fester Tag; ohne beides = Tag des Startdatums (Alt-Regeln).
  let monthlyMode: MonthlyMode = 'monthday';
  let monthDay = defaults.monthDay;
  let monthOrdinal = defaults.monthOrdinal;
  let monthWeekday = defaults.monthWeekday;
  if (freq === 'MONTHLY') {
    const ordMatch = bydayRaw.length === 1 ? /^(-?\d)([A-Z]{2})$/.exec(bydayRaw[0]!) : null;
    if (ordMatch) {
      monthlyMode = 'weekday';
      monthOrdinal = Number(ordMatch[1]);
      monthWeekday = ordMatch[2]!;
    } else if (map.has('BYMONTHDAY')) {
      monthDay = Number(map.get('BYMONTHDAY')) || defaults.monthDay;
    }
  }
  const byday = bydayRaw.filter((b) => /^[A-Z]{2}$/.test(b));
  return {
    freq,
    interval,
    byday: byday.length > 0 ? byday : [weekdayCode(startDate)],
    endMode,
    until,
    count,
    monthlyMode,
    monthDay,
    monthOrdinal,
    monthWeekday,
  };
}

export default function RecurrenceEditor({ startDate, value, onChange }: Props) {
  const initial = parseRrule(value, startDate);
  const [freq, setFreq] = useState<RecurrenceFreq>(initial.freq);
  const [interval, setInterval] = useState<number>(initial.interval);
  const [byday, setByday] = useState<string[]>(initial.byday);
  const [endMode, setEndMode] = useState<EndMode>(initial.endMode);
  const [until, setUntil] = useState<string>(initial.until);
  const [count, setCount] = useState<number>(initial.count);
  const [monthlyMode, setMonthlyMode] = useState<MonthlyMode>(initial.monthlyMode);
  const [monthDay, setMonthDay] = useState<number>(initial.monthDay);
  const [monthOrdinal, setMonthOrdinal] = useState<number>(initial.monthOrdinal);
  const [monthWeekday, setMonthWeekday] = useState<string>(initial.monthWeekday);

  // onChange bei jedem State-Wechsel
  useEffect(() => {
    onChange(buildRrule({ freq, interval, byday, endMode, until, count, monthlyMode, monthDay, monthOrdinal, monthWeekday }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [freq, interval, byday.join(','), endMode, until, count, monthlyMode, monthDay, monthOrdinal, monthWeekday]);

  const toggleDay = (key: string): void => {
    setByday((cur) => cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key]);
  };

  return (
    <div className="space-y-3 rounded-md border border-default bg-muted/30 p-3">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm text-muted-foreground">Wiederholt sich</span>
        <span className="text-sm">alle</span>
        <input
          type="number"
          min={1}
          value={interval}
          onChange={(e) => setInterval(Math.max(1, Number(e.target.value) || 1))}
          className="w-14 h-9 px-2 rounded border border-default bg-background text-foreground text-sm text-center"
        />
        <select
          value={freq}
          onChange={(e) => setFreq(e.target.value as RecurrenceFreq)}
          className="h-9 px-2 rounded border border-default bg-background text-foreground text-sm"
        >
          <option value="DAILY">Tage</option>
          <option value="WEEKLY">Wochen</option>
          <option value="MONTHLY">Monate</option>
          <option value="YEARLY">Jahre</option>
        </select>
      </div>

      {freq === 'WEEKLY' && (
        <div>
          <div className="text-xs text-muted-foreground mb-1.5">An welchen Tagen</div>
          <div className="flex gap-1 flex-wrap">
            {WEEKDAYS.map((d) => {
              const active = byday.includes(d.key);
              return (
                <button
                  key={d.key}
                  type="button"
                  onClick={() => toggleDay(d.key)}
                  className={`w-9 h-9 rounded text-xs font-medium ${
                    active
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-background border border-default text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {d.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {freq === 'MONTHLY' && (
        <div className="space-y-1.5">
          <div className="text-xs text-muted-foreground">Im Monat</div>
          <label className="flex items-center gap-2 text-sm">
            <input type="radio" checked={monthlyMode === 'monthday'} onChange={() => setMonthlyMode('monthday')} />
            <span>am Tag</span>
            <input
              type="number"
              min={1}
              max={31}
              value={monthDay}
              disabled={monthlyMode !== 'monthday'}
              onChange={(e) => { setMonthlyMode('monthday'); setMonthDay(Math.min(31, Math.max(1, Number(e.target.value) || 1))); }}
              className="w-16 h-9 px-2 rounded border border-default bg-background text-foreground text-sm text-center disabled:opacity-50"
            />
          </label>
          <label className="flex items-center gap-2 text-sm flex-wrap">
            <input type="radio" checked={monthlyMode === 'weekday'} onChange={() => setMonthlyMode('weekday')} />
            <span>am</span>
            <select
              value={monthOrdinal}
              disabled={monthlyMode !== 'weekday'}
              onChange={(e) => { setMonthlyMode('weekday'); setMonthOrdinal(Number(e.target.value)); }}
              className="h-9 px-2 rounded border border-default bg-background text-foreground text-sm disabled:opacity-50"
            >
              {ORDINALS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <select
              value={monthWeekday}
              disabled={monthlyMode !== 'weekday'}
              onChange={(e) => { setMonthlyMode('weekday'); setMonthWeekday(e.target.value); }}
              className="h-9 px-2 rounded border border-default bg-background text-foreground text-sm disabled:opacity-50"
            >
              {WEEKDAYS.map((d) => <option key={d.key} value={d.key}>{WEEKDAY_LONG[d.key]}</option>)}
            </select>
          </label>
        </div>
      )}

      <div>
        <div className="text-xs text-muted-foreground mb-1.5">Endet</div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="flex items-center gap-1.5 text-sm">
            <input type="radio" checked={endMode === 'never'} onChange={() => setEndMode('never')} />
            nie
          </label>
          <label className="flex items-center gap-1.5 text-sm">
            <input type="radio" checked={endMode === 'until'} onChange={() => setEndMode('until')} />
            am
          </label>
          <input
            type="date"
            value={until}
            onChange={(e) => { setEndMode('until'); setUntil(e.target.value); }}
            className="h-9 px-2 rounded border border-default bg-background text-foreground text-sm"
          />
          <label className="flex items-center gap-1.5 text-sm">
            <input type="radio" checked={endMode === 'count'} onChange={() => setEndMode('count')} />
            nach
          </label>
          <input
            type="number"
            min={1}
            value={count}
            onChange={(e) => { setEndMode('count'); setCount(Math.max(1, Number(e.target.value) || 1)); }}
            className="w-16 h-9 px-2 rounded border border-default bg-background text-foreground text-sm text-center"
          />
          <span className="text-sm text-muted-foreground">Wiederholungen</span>
        </div>
      </div>
    </div>
  );
}
